import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { getDestinations, getCategories, getCountries } from "../api";
import DestinationCard from "./DestinationCard";
import { useI18n, useL } from "../i18n";

export default function DestinationsGallery() {
  const { t, lang } = useI18n();
  const L = useL();
  const [destinations, setDestinations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [country, setCountry] = useState("all");
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getCountries(), getCategories()]).then(([c, cat]) => {
      setCountries(c);
      setCategories(cat);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    getDestinations({ country, category, q })
      .then(setDestinations)
      .finally(() => setLoading(false));
  }, [country, category, q]);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-20" id="explore">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#C85A32] font-semibold">{t("trending")}</div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl text-[#1C1917]">{t("popular_destinations")}</h2>
        </div>

        <div className="relative w-full md:w-80">
          <Search className="absolute top-1/2 -translate-y-1/2 start-4 h-4 w-4 text-[#78716C]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search_placeholder")}
            data-testid="destinations-search"
            className="w-full h-12 ps-11 pe-4 rounded-full bg-white border border-[#E6DED3] focus:border-[#1C1917] outline-none text-sm"
          />
        </div>
      </div>

      {/* Country pills */}
      <div className="flex gap-2 overflow-x-auto scrollbar-none pb-2 -mx-1 px-1">
        <FilterPill active={country === "all"} onClick={() => setCountry("all")} testid="country-all">
          {t("all_countries")}
        </FilterPill>
        {countries.map((c) => (
          <FilterPill
            key={c.code}
            active={country === c.code}
            onClick={() => setCountry(c.code)}
            testid={`country-${c.code}`}
          >
            <span className="me-1">{c.flag}</span>
            {lang === "ar" ? c.name_ar : c.name_en}
          </FilterPill>
        ))}
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 overflow-x-auto scrollbar-none mt-3 pb-2 -mx-1 px-1">
        {categories.map((c) => (
          <FilterPill
            key={c.id}
            active={category === c.id}
            onClick={() => setCategory(c.id)}
            variant="soft"
            testid={`category-${c.id}`}
          >
            {lang === "ar" ? c.name_ar : c.name_en}
          </FilterPill>
        ))}
      </div>

      {/* Grid */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="destinations-grid">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-96 bg-[#F3EDE4] rounded-3xl animate-pulse" />
            ))
          : destinations.length === 0
            ? (
              <div className="col-span-full text-center py-16 text-[#78716C]">
                {L({
                  ar: "لا توجد نتائج تطابق البحث",
                  en: "No results match your filters",
                  fr: "Aucun résultat ne correspond à vos filtres"
                })}
              </div>
            )
            : destinations.map((d) => <DestinationCard key={d.id} dest={d} />)}
      </div>
    </section>
  );
}

function FilterPill({ children, active, onClick, variant = "solid", testid }) {
  const base = "shrink-0 px-4 h-10 rounded-full text-sm transition-all whitespace-nowrap";
  const styles = active
    ? "bg-[#1C1917] text-[#FAF7F2] border-[#1C1917]"
    : variant === "soft"
      ? "bg-[#F3EDE4] text-[#57534E] hover:bg-[#E6DED3]"
      : "bg-white border border-[#E6DED3] text-[#1C1917] hover:border-[#1C1917]";
  return (
    <button data-testid={testid} onClick={onClick} className={`${base} ${styles}`}>
      {children}
    </button>
  );
}
